import React, { useMemo, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import tw, { css } from "twin.macro";
import { gsap } from "gsap";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { VideoControls } from "~/features/ui/video/video-controls";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { Asset } from "~/features/craft/asset";
import { cleanStegaForTextTransform } from "~/features/sanity/stega";
import ArrowDown from "~/public/icons/arrow-down.svg";
import { Icon } from "~/features/ui/icon";

type SplitTextConstructor = new (target: Element, vars: { type: string }) => { revert: () => void };

let splitTextPromise: Promise<SplitTextConstructor> | null = null;

function loadSplitText() {
  splitTextPromise ??= import("gsap/dist/SplitText").then((mod) => {
    gsap.registerPlugin(mod.SplitText);
    return mod.SplitText;
  });
  return splitTextPromise;
}

if (typeof window !== "undefined") void loadSplitText();

function escapeHtml(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c)
  );
}

// Mirrors `parseTextWithHighlights` from `~/features/ui/headline` but produces
// an HTML string. We populate the SplitText target via `dangerouslySetInnerHTML`
// so React never tracks fibers for the descendants GSAP mutates — otherwise
// `SplitText.revert()` (which calls `element.innerHTML = original`) detaches
// React-tracked nodes and the next reconciliation throws
// `Failed to execute 'removeChild' on 'Node'`.
function headlineToHtml(text: string) {
  return cleanStegaForTextTransform(text)
    .split("\n")
    .map((line) =>
      line
        .split(/[[\]]/)
        .map((part, idx) =>
          idx % 2 === 1 ? `<span style="color:#6C7061">${escapeHtml(part)}</span>` : escapeHtml(part)
        )
        .join("")
    )
    .join("<br/>");
}

const fragment = graphql(`
  fragment Rev8Hero on hero_rev8Hero_BlockType {
    headline: baseHeadline
    caption: baseRichText
    media: baseMedia {
      ...Media
    }
    link: baseLink {
      ...Link
    }
    supportLink: baseLink2 {
      ...Link
    }
    logo: baseImage {
      ...Asset
    }
  }
`);

function wrapLinesWithMask(lines: HTMLElement[]) {
  lines.forEach((line) => {
    const wrapper = document.createElement("div");
    wrapper.style.overflow = "hidden";
    wrapper.style.display = "block";
    wrapper.style.paddingTop = "0.12em";
    wrapper.style.paddingBottom = "0.08em";
    line.parentNode!.insertBefore(wrapper, line);
    wrapper.appendChild(line);
  });
}

const discoverBounce = css`
  display: inline-flex;
  @keyframes rev8DiscoverBounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(2px);
    }
  }
  @media (prefers-reduced-motion: no-preference) {
    animation: rev8DiscoverBounce 1s ease-in-out infinite;
  }
`;

type Rev8HeroProps = FragmentType<typeof fragment> & {
  // `data-sanity` edit attributes injected by `SanityHero`. The headline/caption
  // text is stega-cleaned (SplitText + innerHTML), so the overlay click-to-edit
  // target comes from these attributes instead of the rendered text.
  headlineEditAttribute?: string;
  captionEditAttribute?: string;
};

export function Rev8Hero(props: Rev8HeroProps) {
  const {
    headline,
    caption,
    media: fullscreenMedia,
    link: _link,
    supportLink: _supportLink,
    logo: _logo,
  } = useFragment(fragment, props);

  const { headlineEditAttribute, captionEditAttribute } = props;

  const logo = takeFirst(_logo);

  const link = takeFirst(_link);
  const supportLink = takeFirst(_supportLink);
  const firstMedia = takeFirst(fullscreenMedia);
  const isInlineVideo =
    firstMedia &&
    (("videoUrl" in firstMedia && !!firstMedia.videoUrl) ||
      ("videoAsset" in firstMedia && hasItems(firstMedia.videoAsset)));
  const icon = useLinkIcon(link || undefined);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const { isAnnouncementBarVisible } = useNavLayout();
  const topSpacing = isAnnouncementBarVisible
    ? tw`mt-[calc(var(--header-height) + var(--announcement-bar-height) + 56px)] lg:mt-[calc(var(--header-height) + var(--announcement-bar-height))]`
    : tw`mt-[calc(var(--header-height) + 56px)] lg:mt-[var(--header-height)]`;

  const heroRootRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [introFinished, setIntroFinished] = useState(false);

  const handleScrollDown = () => {
    const next = heroRootRef.current?.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  const headlineHtml = useMemo(() => (headline ? { __html: headlineToHtml(headline) } : undefined), [headline]);
  const headlineText = useMemo(() => (headline ? cleanStegaForTextTransform(headline) : undefined), [headline]);
  const captionHtml = useMemo(() => (caption ? { __html: cleanStegaForTextTransform(caption) } : undefined), [caption]);

  useIsomorphicLayoutEffect(() => {
    if (!heroRootRef.current || !lineRef.current || !headlineRef.current || !actionsRef.current || !logoRef.current)
      return;

    if (caption && !captionRef.current) return;

    const heroRoot = heroRootRef.current;

    let ctx: gsap.Context | undefined;
    let headlineSplit: { revert: () => void } | undefined;
    let captionSplit: { revert: () => void } | undefined;
    let cancelled = false;
    let mainTimeline: gsap.core.Timeline | undefined;

    setIntroFinished(false);

    void loadSplitText()
      .then((SplitText) => {
        if (cancelled) return;

        headlineSplit = new SplitText(headlineRef.current!, { type: "lines" });
        const captionEl = caption ? captionRef.current : null;
        captionSplit = captionEl ? new SplitText(captionEl, { type: "lines" }) : undefined;

        const headlineLines = (headlineSplit as unknown as { lines: HTMLElement[] }).lines;
        const captionLines = captionSplit ? (captionSplit as unknown as { lines: HTMLElement[] }).lines : [];

        wrapLinesWithMask(headlineLines);
        if (captionLines.length) wrapLinesWithMask(captionLines);

        ctx = gsap.context(() => {
          const lineDuration = 3;
          const revealEase = "power4.out";
          const revealDuration = 1.5;
          const lineHide = 135;

          gsap.set(logoRef.current, { autoAlpha: 0, y: 28, force3D: true });
          gsap.set(lineRef.current, { scaleX: 0, transformOrigin: "left center" });
          if (headlineLines.length) {
            gsap.set(headlineLines, { yPercent: lineHide, force3D: true });
          } else {
            gsap.set(headlineRef.current, { yPercent: lineHide, force3D: true });
          }
          if (captionLines.length) gsap.set(captionLines, { yPercent: lineHide, force3D: true });
          else if (captionEl) gsap.set(captionEl, { yPercent: lineHide, force3D: true });
          gsap.set(actionsRef.current, { autoAlpha: 0, y: 28, force3D: true });

          gsap.set(headlineRef.current, { autoAlpha: 1 });
          if (captionEl) gsap.set(captionEl, { autoAlpha: 1 });

          const tl = gsap.timeline({
            delay: 0.3,
            onComplete: () => {
              if (!cancelled) setIntroFinished(true);
            },
          });
          mainTimeline = tl;

          tl.to(lineRef.current, {
            scaleX: 1,
            duration: lineDuration,
            ease: "power2.inOut",
          });

          // Line reaching left column — logo + headline
          tl.to(logoRef.current, { autoAlpha: 1, y: 0, duration: revealDuration, ease: revealEase }, 0.8);
          if (headlineLines.length) {
            tl.to(headlineLines, { yPercent: 0, duration: revealDuration, stagger: 0.1, ease: revealEase }, 0.85);
          } else {
            tl.to(headlineRef.current, { yPercent: 0, duration: revealDuration, ease: revealEase }, 0.85);
          }

          // Line sweeping through center — caption
          if (captionLines.length) {
            tl.to(captionLines, { yPercent: 0, duration: revealDuration, stagger: 0.08, ease: revealEase }, 1.4);
          } else if (captionEl) {
            tl.to(captionEl, { yPercent: 0, duration: revealDuration, ease: revealEase }, 1.4);
          }

          // Line reaching right side — actions
          tl.to(actionsRef.current, { autoAlpha: 1, y: 0, duration: revealDuration, ease: revealEase }, 2.0);
        }, heroRoot);
      })
      .catch(() => {
        if (cancelled || !headlineRef.current) return;
        headlineRef.current.classList.remove("invisible");
        if (captionRef.current) captionRef.current.classList.remove("invisible");
        setIntroFinished(true);
      });

    return () => {
      cancelled = true;
      mainTimeline?.kill();
      ctx?.revert();
      headlineSplit?.revert();
      captionSplit?.revert();
      setIntroFinished(false);
    };
  }, [headline, caption]);

  return (
    <div ref={heroRootRef} data-theme="dark" tw="border-t-0! text-primary relative">
      <div tw="flex min-h-screen items-end lg:min-h-screen lg:items-center">
        <div tw="absolute inset-0">
          <Media
            {...takeFirst(fullscreenMedia)}
            imageProps={{
              fit: "cover",
              rounded: false,
              loading: "eager",
              priority: true,
              fetchPriority: "high",
              sizes: "100vw",
              imgixProps: { q: 30 },
            }}
            inlineVideoProps={{ fit: "cover", videoRef: fullscreenVideoRef, rounded: false, priority: true }}
            externalVideoProps={{ fit: "cover", rounded: false, background: true }}
          />
        </div>
        <div tw="flex w-full flex-1 flex-col gap-md pb-24 lg:pb-0" css={[topSpacing]}>
          <div tw="flex flex-col justify-start gap-sm overflow-hidden px-md lg:px-xl">
            <div ref={logoRef} tw="opacity-0 translate-y-28 [&_img]:max-h-[24px] [&_img]:w-auto">
              {logo && (
                <Asset
                  asset={logo}
                  imageProps={{ rounded: false, fit: "contain", priority: true, fetchPriority: "high" }}
                />
              )}
            </div>

            <div tw="flex w-full justify-start overflow-hidden">
              {headline && headlineHtml && (
                <h1
                  ref={headlineRef}
                  data-sanity={headlineEditAttribute}
                  css={[
                    tw`text-mobile-title-30-normal lg:text-desktop-title-60-normal max-w-[22ch]`,
                    !introFinished && tw`invisible`,
                  ]}
                  dangerouslySetInnerHTML={headlineHtml}
                />
              )}
            </div>
          </div>
          <div tw="px-md lg:px-xl">
            <div ref={lineRef} tw="h-px w-full origin-left scale-x-0 bg-current opacity-30" />
          </div>
          <div tw="relative z-10 flex w-full flex-1 flex-col items-stretch gap-24 px-md text-left lg:grid lg:grid-cols-2 lg:items-stretch lg:justify-between lg:gap-24 lg:px-xl">
            <div tw="w-full overflow-hidden text-left">
              {caption && captionHtml && (
                <div
                  ref={captionRef}
                  role="group"
                  data-sanity={captionEditAttribute}
                  css={[tw`max-w-full space-y-24`, !introFinished && tw`invisible`]}
                  dangerouslySetInnerHTML={captionHtml}
                />
              )}
            </div>
            <div tw="flex w-full justify-start overflow-hidden lg:flex lg:flex-1 lg:items-center lg:justify-end">
              <div
                ref={actionsRef}
                tw="flex flex-row flex-wrap items-center justify-start gap-sm lg:justify-end opacity-0 translate-y-28"
              >
                {isInlineVideo && <VideoControls size="medium" variant="tertiary" videoRef={fullscreenVideoRef} />}
                <div tw="flex flex-row flex-wrap items-center justify-start gap-md lg:justify-end">
                  {isLinkValid(link) && (
                    <Button
                      as={Link}
                      {...link}
                      aria-label={headlineText ? `Learn more: ${headlineText}` : undefined}
                      variant="secondary"
                      icon={icon}
                    />
                  )}
                  {isLinkValid(supportLink) && (
                    <Button
                      as={Link}
                      {...supportLink}
                      aria-label={headlineText ? `Learn more: ${headlineText}` : undefined}
                      variant="underline"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleScrollDown}
            aria-label="Scroll to next section"
            css={[
              tw`relative z-10 mt-md flex flex-row items-center justify-start gap-8 px-md text-current transition-opacity duration-500 hover:opacity-70 lg:absolute lg:bottom-32 lg:right-xl lg:mt-0 lg:px-0`,
              introFinished ? tw`opacity-100` : tw`pointer-events-none opacity-0`,
            ]}
          >
            <span tw="text-mobile-body-20-normal lg:text-desktop-body-20-normal">Discover more</span>
            <span css={discoverBounce}>
              <Icon icon={ArrowDown} size="16" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
